import { useCallback, useEffect, useState } from "react";
import { Loader2, Search, ShieldAlert, UserPlus, UsersRound } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INTERNAL_ROLES, ROLE_LABELS, STATUS_META, apiMessage } from "@/lib/team";
import { TeamTable } from "@/components/admin/team/TeamTable";
import { AddMemberDialog } from "@/components/admin/team/AddMemberDialog";
import { TeamActionDialog } from "@/components/admin/team/TeamActionDialog";

export function SuperAdminOnly() {
  return (
    <div className="pf-card p-10 text-center" data-testid="team-forbidden">
      <ShieldAlert className="w-8 h-8 mx-auto text-slate-400" />
      <p className="mt-3 font-semibold text-slate-900">Super Admins only</p>
      <p className="text-sm text-slate-500 mt-1">Team Management is limited to Cyannect Super Admin accounts.</p>
    </div>
  );
}

export function TwoFactorNotice() {
  return (
    <div className="mt-6 flex gap-2 text-sm text-amber-800 bg-amber-100 rounded-2xl px-4 py-3" data-testid="team-2fa-notice">
      <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
      <span>Two-step sign-in isn't enforced for Cyannect admin accounts yet. Every team member signs in with email and password.</span>
    </div>
  );
}

const sel = "pf-input px-3 py-2 text-sm";

export default function TeamList() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("created");
  const [adding, setAdding] = useState(false);
  const [action, setAction] = useState(null);
  const allowed = user?.role === "super_admin";

  const load = useCallback(() => {
    const qs = new URLSearchParams({ sort });
    if (q.trim()) qs.set("q", q.trim());
    if (role) qs.set("role", role);
    if (status) qs.set("status", status);
    setError("");
    return api.get(`/admin/team?${qs}`).then((r) => setData(r.data)).catch((e) => setError(apiMessage(e, "Couldn't load the team.")));
  }, [q, role, status, sort]);

  useEffect(() => {
    if (!allowed) return undefined;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, allowed]);

  if (!allowed) return <SuperAdminOnly />;
  const isOwner = !!data?.actor_is_owner;
  const done = () => { setAdding(false); setAction(null); load(); };

  return (
    <div data-testid="admin-team">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="pf-chip pf-chip-cyan"><UsersRound className="w-3 h-3" /> Settings · Team Management</div>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900" data-testid="team-title">Cyannect Team</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Internal Cyannect staff only. Merchants and shoppers never appear here, and nothing here touches the CRM.</p>
        </div>
        <button type="button" onClick={() => setAdding(true)} className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="team-add-btn">
          <UserPlus className="w-4 h-4" /> Add team member
        </button>
      </div>
      <TwoFactorNotice />

      {data && (
        <div className="mt-6 flex gap-2 flex-wrap" data-testid="team-counts">
          {Object.entries(STATUS_META).map(([k, [label]]) => (
            <button key={k} type="button" onClick={() => setStatus(status === k ? "" : k)} data-testid={`team-count-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${status === k ? "pf-btn-primary" : "pf-btn-glass"}`}>
              {label} <span className="tabular-nums">{data.counts?.[k] ?? 0}</span>
            </button>
          ))}
        </div>
      )}

      <div className="mt-4 flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400 z-10 pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" aria-label="Search team" className="pf-input w-full pl-9 pr-3 py-2 text-sm" data-testid="team-search" />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Filter by role" className={sel} data-testid="team-filter-role">
          <option value="">All roles</option>
          {INTERNAL_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status" className={sel} data-testid="team-filter-status">
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
        </select>
      </div>

      {error && (
        <div className="mt-6 pf-card p-6 text-sm text-red-700 flex items-center justify-between gap-3" role="alert" data-testid="team-error">
          {error}<button type="button" onClick={load} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold" data-testid="team-retry">Retry</button>
        </div>
      )}
      {!data && !error && <div className="mt-10 flex justify-center text-slate-500" data-testid="team-loading"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {data && <TeamTable rows={data.items} sort={sort} onSort={setSort} actor={user} actorIsOwner={isOwner} onAction={(kind, row) => setAction({ kind, row })} />}

      <AddMemberDialog open={adding} onClose={() => setAdding(false)} onDone={done} actorIsOwner={isOwner} />
      <TeamActionDialog action={action} actorIsOwner={isOwner} onClose={() => setAction(null)} onDone={done} />
    </div>
  );
}
